import React, { useEffect, useState, useRef } from 'react';
import { X, Plus, Paperclip, FileText, CheckCircle, Sparkles, Wrench, Calendar, Clock, MapPin, User, Package } from 'lucide-react';
import { useContracts } from '../context/ContractsContext';
interface NuovoInterventoFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}
type InterventionType = 'pulizia' | 'manutenzione';
type Mode = 'singolo' | 'multiplo';
interface SingleInterventoDraft {
  tipo: InterventionType;
  data: string;
  ora: string;
  luogo: string;
  note: string;
}
interface AttachedContract {
  name: string;
  size: number;
}
// Lightweight CRM client list (subset of the Clienti page roster)
interface CrmClient {
  id: string;
  name: string;
}
const CRM_CLIENTS: CrmClient[] = [{
  id: '1',
  name: 'Marco Rossi'
}, {
  id: '2',
  name: 'Laura Bianchi'
}, {
  id: '3',
  name: 'Giuseppe Verdi'
}, {
  id: '4',
  name: 'Elena Colombo'
}, {
  id: '5',
  name: 'Alessandro Ferrari'
}, {
  id: '6',
  name: 'Chiara Russo'
}, {
  id: '7',
  name: 'Roberto Romano'
}, {
  id: '8',
  name: 'Silvia Gallo'
}];
// Package sizes a contract can cover
const PACKAGE_SIZES = [5, 10, 15, 20, 30];
function emptySingle(): SingleInterventoDraft {
  return {
    tipo: 'pulizia',
    data: '',
    ora: '',
    luogo: '',
    note: ''
  };
}
function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
export function NuovoInterventoFormModal({
  isOpen,
  onClose
}: NuovoInterventoFormModalProps) {
  const [mode, setMode] = useState<Mode>('singolo');
  // Singolo: a single intervento
  const [single, setSingle] = useState<SingleInterventoDraft>(emptySingle());
  // Multiplo (pacchetto): how many interventi + which client
  const [packageSize, setPackageSize] = useState<number>(10);
  const [clientId, setClientId] = useState<string>('');
  const [customClient, setCustomClient] = useState('');
  const [packageZone, setPackageZone] = useState('');
  const [contract, setContract] = useState<AttachedContract | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    addContract
  } = useContracts();
  // Reset everything whenever the modal is (re)opened
  useEffect(() => {
    if (isOpen) {
      setMode('singolo');
      setSingle(emptySingle());
      setPackageSize(10);
      setClientId('');
      setCustomClient('');
      setPackageZone('');
      setContract(null);
      setIsSubmitting(false);
      setSubmitted(false);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const updateSingle = (patch: Partial<SingleInterventoDraft>) => setSingle(prev => ({
    ...prev,
    ...patch
  }));
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setContract({
        name: file.name,
        size: file.size
      });
    }
    // Allow re-selecting the same file later
    e.target.value = '';
  };
  // Resolve the chosen client's display name
  const resolvedClientName = clientId === 'altro' ? customClient.trim() : CRM_CLIENTS.find(c => c.id === clientId)?.name ?? '';
  // Validation depends on the mode
  const isValid = mode === 'singolo' ? single.tipo !== '' && single.data.trim() !== '' && single.luogo.trim() !== '' : packageSize > 0 && resolvedClientName !== '';
  const handleSubmit = () => {
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    setTimeout(() => {
      // Persist the attached contract so it shows up in the CM's
      // "Contratti attivi" section.
      if (contract) {
        if (mode === 'multiplo') {
          addContract({
            zone: packageZone.trim() || resolvedClientName || 'Nuovo cliente',
            size: packageSize,
            unitPrice: 45,
            done: 0,
            fileName: contract.name
          });
        } else {
          const rawZone = single.luogo.trim();
          const zone = rawZone.split(',').map(s => s.trim()).filter(Boolean).pop() || rawZone || 'Nuova zona';
          addContract({
            zone,
            size: 1,
            unitPrice: 45,
            done: 0,
            fileName: contract.name
          });
        }
      }
      setIsSubmitting(false);
      setSubmitted(true);
    }, 1400);
  };
  const submitLabel = mode === 'multiplo' ? `Salva pacchetto · ${packageSize} interventi` : 'Salva intervento';
  return <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200" onClick={onClose} data-id="element-340">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Nuovo intervento" data-id="element-341">
        {/* Header */}
        <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-200" data-id="element-342">
          <div data-id="element-343">
            <h2 className="text-lg font-bold text-navy" data-id="element-344">Nuovo intervento</h2>
            {!submitted && <p className="text-xs text-gray-500 mt-0.5" data-id="element-345">
                Inserisci un intervento singolo o un pacchetto su contratto
              </p>}
          </div>
          <button onClick={onClose} aria-label="Chiudi" className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-346">
            <X size={20} className="text-gray-500" data-id="element-347" />
          </button>
        </div>

        {/* Success state */}
        {submitted ? <div className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-348">
            <div className="text-center py-4" data-id="element-349">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-350">
                <CheckCircle size={32} className="text-green-600" data-id="element-351" />
              </div>
              <h3 className="text-lg font-bold text-navy mb-2" data-id="element-352">
                {mode === 'multiplo' ? 'Pacchetto salvato!' : 'Intervento salvato!'}
              </h3>
              <p className="text-sm text-gray-600 mb-6" data-id="element-353">
                {contract ? 'I dati e il contratto allegato sono stati registrati.' : 'I dati sono stati registrati con successo.'}
              </p>

              <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2 mb-4" data-id="element-354">
                <div className="flex justify-between text-sm" data-id="element-355">
                  <span className="text-gray-600" data-id="element-356">Tipo richiesta</span>
                  <span className="font-medium text-navy" data-id="element-357">
                    {mode === 'multiplo' ? 'Pacchetto' : 'Singolo'}
                  </span>
                </div>
                {mode === 'multiplo' ? <>
                    <div className="flex justify-between text-sm" data-id="element-358">
                      <span className="text-gray-600" data-id="element-359">Cliente</span>
                      <span className="font-medium text-navy truncate ml-3" data-id="element-360">
                        {resolvedClientName}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-361">
                      <span className="text-gray-600" data-id="element-362">Interventi previsti</span>
                      <span className="font-medium text-navy" data-id="element-363">
                        {packageSize}
                      </span>
                    </div>
                  </> : <div className="flex justify-between text-sm" data-id="element-364">
                    <span className="text-gray-600" data-id="element-365">Interventi</span>
                    <span className="font-medium text-navy" data-id="element-366">1</span>
                  </div>}
                {contract && <div className="flex justify-between text-sm gap-3" data-id="element-367">
                    <span className="text-gray-600 flex-shrink-0" data-id="element-368">
                      Contratto
                    </span>
                    <span className="font-medium text-navy truncate" data-id="element-369">
                      {contract.name}
                    </span>
                  </div>}
              </div>

              {contract && <div className="flex items-start gap-2 text-xs text-coral bg-coral/5 border border-coral/15 rounded-xl p-3 text-left mb-6" data-id="element-370">
                  <FileText size={14} className="flex-shrink-0 mt-0.5" data-id="element-371" />
                  <span data-id="element-372">
                    Il contratto è stato salvato nella sezione{' '}
                    <span className="font-semibold" data-id="element-373">Contratti</span>.
                  </span>
                </div>}

              <button onClick={onClose} className="w-full px-4 py-3 bg-coral text-white rounded-xl text-sm font-semibold hover:bg-coral-600 transition-colors min-h-[48px]" data-id="element-374">
                Chiudi
              </button>
            </div>
          </div> : <>
            {/* Content (form) */}
            <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-5" data-id="element-375">
              {/* Mode toggle: singolo / multiplo */}
              <div className="grid grid-cols-2 gap-1 p-1 bg-gray-100 rounded-xl" data-id="element-376">
                {([{
              id: 'singolo',
              label: 'Singolo'
            }, {
              id: 'multiplo',
              label: 'Più interventi'
            }] as {
              id: Mode;
              label: string;
            }[]).map(m => <button key={m.id} onClick={() => setMode(m.id)} aria-pressed={mode === m.id} className={`h-9 rounded-lg text-sm font-semibold transition-colors ${mode === m.id ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'}`} data-id="element-377">
                    {m.label}
                  </button>)}
              </div>

              {/* SINGOLO: one intervento */}
              {mode === 'singolo' && <div className="rounded-xl border border-gray-200 p-4 space-y-4" data-id="element-378">
                  {/* Tipo */}
                  <div data-id="element-379">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-380">
                      Tipo di intervento
                    </label>
                    <div className="grid grid-cols-2 gap-2" data-id="element-381">
                      {([{
                  id: 'pulizia',
                  label: 'Pulizia',
                  icon: <Sparkles size={16} className="text-coral" data-id="element-382" />
                }, {
                  id: 'manutenzione',
                  label: 'Manutenzione',
                  icon: <Wrench size={16} className="text-amber-600" data-id="element-383" />
                }] as {
                  id: InterventionType;
                  label: string;
                  icon: React.ReactNode;
                }[]).map(t => {
                  const active = single.tipo === t.id;
                  return <button key={t.id} onClick={() => updateSingle({
                    tipo: t.id
                  })} aria-pressed={active} className={`flex items-center gap-2 px-3 h-11 rounded-xl border-2 text-sm font-medium transition-all ${active ? 'border-coral bg-coral/5 text-navy' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-384">
                            {t.icon}
                            {t.label}
                          </button>;
                })}
                    </div>
                  </div>

                  {/* Data + Ora */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-385">
                    <div data-id="element-386">
                      <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-387">
                        Data
                      </label>
                      <div className="relative" data-id="element-388">
                        <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-389" />
                        <input type="date" value={single.data} onChange={e => updateSingle({
                    data: e.target.value
                  })} className="w-full h-11 pl-9 pr-3 rounded-xl border border-gray-200 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral" data-id="element-390" />
                      </div>
                    </div>
                    <div data-id="element-391">
                      <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-392">
                        Orario
                      </label>
                      <div className="relative" data-id="element-393">
                        <Clock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-394" />
                        <input type="time" value={single.ora} onChange={e => updateSingle({
                    ora: e.target.value
                  })} className="w-full h-11 pl-9 pr-3 rounded-xl border border-gray-200 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral" data-id="element-395" />
                      </div>
                    </div>
                  </div>

                  {/* Luogo */}
                  <div data-id="element-396">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-397">
                      Indirizzo / Proprietà
                    </label>
                    <div className="relative" data-id="element-398">
                      <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-399" />
                      <input type="text" value={single.luogo} onChange={e => updateSingle({
                  luogo: e.target.value
                })} placeholder="Es. Via Vigevano 18, Milano" className="w-full h-11 pl-9 pr-3 rounded-xl border border-gray-200 text-sm text-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral" data-id="element-400" />
                    </div>
                  </div>

                  {/* Note */}
                  <div data-id="element-401">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-402">
                      Note <span className="text-gray-400" data-id="element-403">(opzionale)</span>
                    </label>
                    <textarea value={single.note} onChange={e => updateSingle({
                note: e.target.value
              })} rows={2} placeholder="Dettagli aggiuntivi per l'operatore" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-navy placeholder:text-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral" data-id="element-404" />
                  </div>
                </div>}

              {/* MULTIPLO: package on a CRM client */}
              {mode === 'multiplo' && <div className="rounded-xl border border-gray-200 p-4 space-y-4" data-id="element-405">
                  {/* Numero interventi nel pacchetto */}
                  <div data-id="element-406">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-407">
                      Quanti interventi prevede il pacchetto?
                    </label>
                    <div className="relative" data-id="element-408">
                      <Package size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-409" />
                      <select value={packageSize} onChange={e => setPackageSize(Number(e.target.value))} className="w-full h-11 pl-9 pr-3 rounded-xl border border-gray-200 text-sm text-navy bg-white focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral appearance-none" data-id="element-410">
                        {PACKAGE_SIZES.map(n => <option key={n} value={n} data-id="element-411">
                            {n} interventi
                          </option>)}
                      </select>
                    </div>
                  </div>

                  {/* Cliente (dal CRM) */}
                  <div data-id="element-412">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-413">
                      Cliente{' '}
                      <span className="text-gray-400" data-id="element-414">(dal tuo CRM)</span>
                    </label>
                    <div className="relative" data-id="element-415">
                      <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-416" />
                      <select value={clientId} onChange={e => setClientId(e.target.value)} className="w-full h-11 pl-9 pr-3 rounded-xl border border-gray-200 text-sm text-navy bg-white focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral appearance-none" data-id="element-417">
                        <option value="" data-id="element-418">Seleziona un cliente…</option>
                        {CRM_CLIENTS.map(c => <option key={c.id} value={c.id} data-id="element-419">
                            {c.name}
                          </option>)}
                        <option value="altro" data-id="element-420">Cliente non in lista…</option>
                      </select>
                    </div>
                    {clientId === 'altro' && <input type="text" value={customClient} onChange={e => setCustomClient(e.target.value)} placeholder="Nome del nuovo cliente" className="w-full h-11 px-3 mt-2 rounded-xl border border-gray-200 text-sm text-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral" data-id="element-421" />}
                  </div>

                  {/* Zona / indirizzo (opzionale) */}
                  <div data-id="element-422">
                    <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-423">
                      Zona / indirizzo{' '}
                      <span className="text-gray-400" data-id="element-424">(opzionale)</span>
                    </label>
                    <div className="relative" data-id="element-425">
                      <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-426" />
                      <input type="text" value={packageZone} onChange={e => setPackageZone(e.target.value)} placeholder="Es. Milano · Navigli" className="w-full h-11 pl-9 pr-3 rounded-xl border border-gray-200 text-sm text-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral" data-id="element-427" />
                    </div>
                  </div>
                </div>}

              {/* Contract attachment */}
              <div data-id="element-428">
                <label className="block text-xs font-medium text-gray-600 mb-1.5" data-id="element-429">
                  Contratto{' '}
                  <span className="text-gray-400" data-id="element-430">
                    {mode === 'multiplo' ? '' : '(opzionale)'}
                  </span>
                </label>
                <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,image/*" onChange={handleFileChange} className="sr-only" aria-label="Allega contratto" data-id="element-431" />
                {contract ? <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3" data-id="element-432">
                    <span className="w-10 h-10 rounded-xl bg-coral/10 flex items-center justify-center flex-shrink-0" data-id="element-433">
                      <FileText size={18} className="text-coral" data-id="element-434" />
                    </span>
                    <div className="min-w-0 flex-1" data-id="element-435">
                      <p className="text-sm font-medium text-navy truncate" data-id="element-436">
                        {contract.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-437">
                        {formatSize(contract.size)} · sarà salvato in Contratti
                      </p>
                    </div>
                    <button onClick={() => setContract(null)} aria-label="Rimuovi contratto" className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0" data-id="element-438">
                      <X size={18} data-id="element-439" />
                    </button>
                  </div> : <button onClick={() => fileInputRef.current?.click()} className="w-full flex items-center justify-center gap-2 h-12 rounded-xl border-2 border-dashed border-gray-300 text-sm font-medium text-gray-600 hover:border-coral hover:text-coral transition-colors" data-id="element-440">
                    <Paperclip size={16} data-id="element-441" />
                    Allega contratto (PDF, DOC)
                  </button>}
                <p className="text-[11px] text-gray-400 mt-1.5" data-id="element-442">
                  Il contratto allegato verrà salvato nella sezione Contratti.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-4 lg:p-6 border-t border-gray-200" data-id="element-443">
              <button onClick={onClose} className="px-4 h-11 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors" data-id="element-444">
                Annulla
              </button>
              <button onClick={handleSubmit} disabled={!isValid || isSubmitting} className={`px-5 h-11 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${isValid && !isSubmitting ? 'bg-coral text-white hover:bg-coral-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-445">
                {isSubmitting ? <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-446" />
                    Salvataggio...
                  </> : <>
                    <CheckCircle size={18} data-id="element-447" />
                    {submitLabel}
                  </>}
              </button>
            </div>
          </>}
      </div>
    </div>;
}